import type { Subscription } from "../../types/subscription";
import { coerceUserSettings, type UserSettingsState } from "../user-settings";
import { getApiBaseUrl } from "./config";

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(text || res.statusText);
  }
}

/**
 * Each GET /v1/auth/csrf sets a new cookie. Concurrent callers (e.g. SessionBridge exchange
 * + ensureApiSession from cloud sync) would rotate the cookie between another caller’s fetch
 * and its POST, producing “Invalid CSRF token”. Serialize CSRF fetch + mutating request.
 */
let csrfGate: Promise<void> = Promise.resolve();

async function withCsrfMutation<T>(
  fn: (csrf: { token: string; header: string }) => Promise<T>,
): Promise<T> {
  const run = async () => {
    await csrfGate;
    let release!: () => void;
    csrfGate = new Promise<void>((resolve) => {
      release = resolve;
    });
    try {
      const base = getApiBaseUrl();
      const csrfRes = await fetch(`${base}/v1/auth/csrf`, {
        credentials: "include",
      });
      if (!csrfRes.ok) throw new Error(`CSRF failed: ${csrfRes.status}`);
      const csrf = await parseJson<{ token: string; header: string }>(csrfRes);
      return await fn(csrf);
    } finally {
      release();
    }
  };

  /** Per-tab mutex is missing other tabs; they share one `st_csrf` cookie on the API host. */
  if (typeof navigator !== "undefined" && navigator.locks?.request) {
    return navigator.locks.request("dripwatch-api-csrf", run);
  }
  return run();
}

export async function exchangeSession(tokens: {
  accessToken?: string;
  idToken?: string;
}): Promise<void> {
  if (!tokens.accessToken && !tokens.idToken) {
    throw new Error("exchangeSession: accessToken or idToken required");
  }
  const base = getApiBaseUrl();
  await withCsrfMutation(async ({ token: csrf, header }) => {
    const res = await fetch(`${base}/v1/auth/session/exchange`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [header]: csrf,
      },
      body: JSON.stringify({
        ...(tokens.accessToken ? { accessToken: tokens.accessToken } : {}),
        ...(tokens.idToken ? { idToken: tokens.idToken } : {}),
      }),
    });
    if (!res.ok) {
      const t = await res.text();
      let msg = t || `Exchange failed: ${res.status}`;
      try {
        const j = JSON.parse(t) as { message?: string; error?: string };
        if (j.message) msg = j.message;
        else if (typeof j.error === "string") msg = j.error;
      } catch {
        /* raw text */
      }
      throw new Error(msg);
    }
  });
}

/** Auth0 SPA hooks — exchange id_token + access_token for API session cookie. */
export type Auth0ExchangeGetters = {
  getAccessTokenSilently: () => Promise<string>;
  getIdTokenClaims: () => Promise<{ __raw?: string } | undefined>;
};

export async function exchangeSessionFromAuth0(getters: Auth0ExchangeGetters): Promise<void> {
  // Sequential: if these run in parallel, getIdTokenClaims can resolve from cache before
  // getAccessTokenSilently finishes refreshing — then __raw is an expired id_token while
  // the access token is fresh, and the API (which prefers id_token) rejects with exp errors.
  const accessToken = await getters.getAccessTokenSilently();
  const idClaims = await getters.getIdTokenClaims();
  const idToken =
    idClaims && typeof idClaims.__raw === "string" ? idClaims.__raw : undefined;
  await exchangeSession({ accessToken, idToken });
}

export async function logoutApiSession(): Promise<void> {
  const base = getApiBaseUrl();
  await withCsrfMutation(async ({ token: csrf, header }) => {
    const res = await fetch(`${base}/v1/auth/logout`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [header]: csrf,
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `Logout failed: ${res.status}`);
    }
  });
}

export type MeResponse = {
  id: string;
  email: string;
  emailVerified: boolean;
  stripeCustomerId: string | null;
};

export async function fetchMe(): Promise<MeResponse | null> {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/v1/me`, { credentials: "include" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(await res.text());
  return parseJson<MeResponse>(res);
}

export async function fetchTrackerSubscriptionsCloud(): Promise<Subscription[]> {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/v1/me/tracker-subscriptions`, {
    credentials: "include",
  });
  if (res.status === 401) {
    throw new Error("Unauthorized");
  }
  if (!res.ok) throw new Error(await res.text());
  const data = await parseJson<{ subscriptions: Subscription[] }>(res);
  return Array.isArray(data.subscriptions) ? data.subscriptions : [];
}

export async function fetchUserSettingsCloud(): Promise<UserSettingsState | null> {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/v1/me/app-settings`, {
    credentials: "include",
  });
  if (res.status === 401) {
    throw new Error("Unauthorized");
  }
  if (!res.ok) throw new Error(await res.text());
  const data = await parseJson<{ settings: unknown }>(res);
  if (data.settings == null) return null;
  return coerceUserSettings(data.settings);
}

export async function putUserSettingsCloud(settings: UserSettingsState): Promise<void> {
  const base = getApiBaseUrl();
  await withCsrfMutation(async ({ token: csrf, header }) => {
    const res = await fetch(`${base}/v1/me/app-settings`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [header]: csrf,
      },
      body: JSON.stringify({ settings }),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `Save failed: ${res.status}`);
    }
  });
}

export async function putTrackerSubscriptionsCloud(
  subscriptions: Subscription[],
): Promise<void> {
  const base = getApiBaseUrl();
  await withCsrfMutation(async ({ token: csrf, header }) => {
    const res = await fetch(`${base}/v1/me/tracker-subscriptions`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [header]: csrf,
      },
      body: JSON.stringify({ subscriptions }),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `Save failed: ${res.status}`);
    }
  });
}

export type EntitlementsResponse = {
  hasLifetimeAccess: boolean;
  hasActiveSubscription: boolean;
  isInGracePeriod: boolean;
  hasProAccess: boolean;
};

export async function fetchEntitlements(): Promise<EntitlementsResponse | null> {
  const base = getApiBaseUrl();
  const res = await fetch(`${base}/v1/entitlements`, { credentials: "include" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(await res.text());
  return parseJson<EntitlementsResponse>(res);
}

export async function createLifetimeCheckout(): Promise<{ url: string | null }> {
  const base = getApiBaseUrl();
  return withCsrfMutation(async ({ token: csrf, header }) => {
    const res = await fetch(`${base}/v1/billing/checkout/lifetime`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [header]: csrf,
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `Checkout failed: ${res.status}`);
    }
    return parseJson<{ url: string | null }>(res);
  });
}

export async function createBillingPortal(): Promise<{ url: string }> {
  const base = getApiBaseUrl();
  return withCsrfMutation(async ({ token: csrf, header }) => {
    const res = await fetch(`${base}/v1/billing/portal`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [header]: csrf,
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) throw new Error(await res.text());
    return parseJson<{ url: string }>(res);
  });
}
